import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2, Clock, CheckCircle2, PackageOpen, ShoppingBag, PoundSterling,
  ScrollText, ArrowLeft, ArrowLeftRight, RotateCcw, ExternalLink, Pencil, Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP, clothingSummary } from "@/lib/marketplace";
import { useAuth } from "@/context/AuthContext";
import ListingBadges from "@/components/ListingBadges";
import PendingApprovalsInbox from "@/components/PendingApprovalsInbox";
import EditListingModal from "@/components/EditListingModal";
import SharedTransactionRow from "@/components/SharedTransactionRow";
import OrderSummaryModal from "@/components/OrderSummaryModal";
import { Switch } from "@/components/ui/switch";
import { subscribeListingUpdates, mergeListingInto } from "@/lib/listingBus";

/**
 * Iteration 79 — Parent Ledger & Activity page.
 *
 * Seven tabs in the exact master-list order:
 *   1. Pending Approvals   → reuses <PendingApprovalsInbox/>
 *   2. Live Listings       → filtered /listings/mine/all + Edit / Delete controls
 *   3. Reserved Items      → filtered /listings/mine/all + Release button
 *   4. Orders              → /orders with Sold vs Bought tags
 *   5. Earnings            → /dashboard/parent aggregation + payouts (FINANCIAL)
 *   6. History Listings    → read-only archive log of every listing ever posted
 *   7. Transfers           → cash-out requests + allowance/wallet top-ups (FINANCIAL)
 *
 * The active tab is mirrored to the URL (`?tab=<key>`) so ParentDashboard
 * stat cards can deep-link into a specific slice.
 */

const TABS = [
  { key: "pending",  label: "Pending Approvals", icon: Clock,         testid: "ledger-tab-pending" },
  { key: "live",     label: "Live Listings",     icon: CheckCircle2,  testid: "ledger-tab-live" },
  { key: "reserved", label: "Reserved Items",    icon: PackageOpen,   testid: "ledger-tab-reserved" },
  // Iteration 166 — Orders tab is now a hard redirect to the canonical
  // `/orders` Sales & Purchases page. Every other tab stays local.
  { key: "orders",   label: "Orders",            icon: ShoppingBag,   testid: "ledger-tab-orders", href: "/orders" },
  { key: "earnings", label: "Earnings",          icon: PoundSterling, testid: "ledger-tab-earnings" },
  { key: "history",  label: "History Listings",  icon: ScrollText,    testid: "ledger-tab-history" },
  // Iteration 278 — dedicated Transfers tab (cash-outs + top-ups).
  { key: "transfers", label: "Transfers", icon: ArrowLeftRight, testid: "ledger-tab-transfers" },
];

function TabHeader({ active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="ledger-tabs">
      {TABS.map((t) => {
        const Icon = t.icon;
        const isActive = t.key === active;
        const cls = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
          isActive
            ? "bg-brandSlate text-white border-brandSlate shadow-sm"
            : "bg-white text-slate-700 border-slate-200 hover:border-brandSlate"
        }`;
        // Iteration 166 — Orders tab redirects instead of swapping local state.
        if (t.href) {
          return (
            <Link key={t.key} to={t.href} className={cls} data-testid={t.testid}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </Link>
          );
        }
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cls}
            data-testid={t.testid}
          >
            <Icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

const Empty = ({ label }) => (
  <div className="card-base p-6 text-center text-sm text-slate-500" data-testid="ledger-empty">{label}</div>
);

function ConfirmDeleteModal({ listing, onClose, onConfirmed }) {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState("");
  const [reason, setReason] = useState("");
  // Iteration 286 — cause/org listings use an audited archive flow.
  const causeLinked = !!(listing.cause_kid_id || listing.seller_account_type === "organization");
  const REASONS = [
    { key: "item_damaged", label: "Item Damaged" },
    { key: "withdrawn", label: "Withdrawn" },
    { key: "sold_elsewhere", label: "Sold Elsewhere" },
    { key: "no_longer_available", label: "No Longer Available" },
  ];

  const submit = async () => {
    setErr("");
    setDeleting(true);
    try {
      await api.delete(`/listings/${listing.id}${causeLinked ? `?reason=${reason}` : ""}`);
      onConfirmed();
    } catch (e2) {
      setErr(formatApiError(e2) || "Couldn't delete this listing.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4" data-testid="delete-listing-modal">
      <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-xl">
        <h3 className="font-display text-xl font-medium text-slate-900 mb-2">{causeLinked ? "Archive this cause listing?" : "Delete this listing?"}</h3>
        <p className="text-sm text-slate-600 mb-4">
          {causeLinked
            ? `“${listing.title}” is linked to a fundraising cause, so archiving it needs an audited reason.`
            : <>&ldquo;{listing.title}&rdquo; will be permanently removed from your shop and the marketplace. This can&rsquo;t be undone.</>}
        </p>
        {causeLinked && (
          <>
            <label className="block text-sm font-bold text-slate-700 mb-1">Archive reason (required)</label>
            <select
              className="input-base mb-4"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="delete-listing-reason"
            >
              <option value="">Select a reason…</option>
              {REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </>
        )}
        {err && <div className="text-rose-700 text-sm mb-3" data-testid="delete-listing-error">{err}</div>}
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100" data-testid="delete-listing-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={deleting || (causeLinked && !reason)} className="px-4 py-2 rounded-full text-sm font-bold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60" data-testid="delete-listing-confirm">
            {deleting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : causeLinked ? "Archive listing" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ListingList({ listings, mode, onRelease, releasingId, onEdit, onDelete, testidPrefix }) {
  if (!listings.length) return <Empty label="Nothing to show here yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testidPrefix}-list`}>
      {listings.map((l) => (
        <div key={l.id} className="card-base p-4 flex flex-col gap-2" data-testid={`${testidPrefix}-row-${l.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-display text-base font-medium text-slate-900 line-clamp-2">{l.title}</div>
            <div className="text-brandSlate font-semibold whitespace-nowrap">{formatGBP(l.price)}</div>
          </div>
          <ListingBadges listing={l} only={["status", "condition", "parcel", "parent_posted"]} />
          <div className="flex items-center flex-wrap gap-2 pt-1">
            <Link
              to={`/listing/${l.id}`}
              className="inline-flex items-center gap-1 text-xs font-bold text-brandSlate hover:underline"
              data-testid={`${testidPrefix}-open-${l.id}`}
            >
              Open <ExternalLink className="w-3 h-3" />
            </Link>
            {mode === "live" && (
              <>
                <button
                  type="button"
                  onClick={() => onEdit(l)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-200 hover:bg-slate-200 transition"
                  data-testid={`${testidPrefix}-edit-${l.id}`}
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(l)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100 transition"
                  data-testid={`${testidPrefix}-delete-${l.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </>
            )}
            {mode === "reserved" && (
              <button
                type="button"
                onClick={() => onRelease(l.id)}
                disabled={releasingId === l.id}
                className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 transition disabled:opacity-60"
                data-testid={`${testidPrefix}-release-${l.id}`}
              >
                {releasingId === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Release
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function OrdersTab({ user, kids }) {
  // Iteration 141 — parent-side ledger now flows through the canonical
  // `/api/parent/ledger` endpoint and renders via the shared
  // `SharedTransactionRow` component (same layout as Kid Zone Orders).
  // Scope filter pills sit above the list so the parent can switch
  // between the full household view and any single child.
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("household");
  // Iteration 143 — Dispatch drawer (Order Summary modal). Any sold
  // row's ship/details CTA opens this drawer with the buyer's name,
  // address, carrier picker, and tracking input.
  const [summaryOrderId, setSummaryOrderId] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/parent/ledger?scope=${encodeURIComponent(scope)}`);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch { setRows([]); }
    setLoading(false);
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const scopePills = useMemo(() => {
    const pills = [{ key: "household", label: "Full Household View", testid: "parent-orders-scope-household" }];
    for (const k of (kids || [])) {
      if (!k?.id) continue;
      pills.push({
        key: k.id,
        label: k.name || "Child",
        testid: `parent-orders-scope-${(k.name || "kid").toLowerCase()}`,
      });
    }
    return pills;
  }, [kids]);

  const handleShipped = () => {
    // Re-fetch so the pill flips from Amber → Green and the CTA swaps
    // from "View Buyer Info & Ship" → "View Order Details".
    load();
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" data-testid="parent-orders-loading" />;
  return (
    <div data-testid="parent-orders-tab">
      <div className="flex flex-wrap gap-2 mb-4" data-testid="parent-orders-scope-bar">
        {scopePills.map((p) => {
          const isActive = p.key === scope;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setScope(p.key)}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                isActive
                  ? "bg-brandSlate text-white border-brandSlate shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-brandSlate"
              }`}
              data-testid={p.testid}
              aria-pressed={isActive}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {rows.length === 0 ? (
        <Empty label="No orders yet — sales and purchases will appear here." />
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {rows.map((row) => (
            <SharedTransactionRow
              key={`${row.id}-${row.role}`}
              row={row}
              viewerRole="parent"
              testIdPrefix="parent-orders"
              onOpenSummary={(orderId) => setSummaryOrderId(orderId)}
            />
          ))}
        </div>
      )}
      <OrderSummaryModal
        orderId={summaryOrderId}
        open={!!summaryOrderId}
        onClose={() => setSummaryOrderId(null)}
        onShipped={handleShipped}
      />
    </div>
  );
}

function EarningsTab() {
  // Iteration 249 — sales-only itemized breakdown. Both the NET
  // EARNINGS card and the itemized list bind to `/ledger/earnings`,
  // whose total is computed as the exact sum of the listed sales, so
  // the two can never drift apart. Refunded orders and family
  // purchases are excluded server-side.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data: resp } = await api.get("/ledger/earnings");
        if (!cancel) setData(resp || {});
      } catch {
        if (!cancel) setData({});
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);
  const netPence = Number(data?.net_earnings_pennies || 0);
  const sales = Array.isArray(data?.sales) ? data.sales : [];
  return (
    <div>
      <div className="card-base p-6 mb-4 bg-emerald-50 border-emerald-100">
        <div className="text-xs font-bold uppercase tracking-wider text-emerald-900">Net earnings (after fees)</div>
        <div className="text-4xl font-display font-semibold text-emerald-900 mt-1" data-testid="earnings-net">{formatGBP(netPence / 100)}</div>
        <div className="text-[11px] text-emerald-800 mt-1">Lifetime household net payout (gross − platform fees)</div>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" data-testid="earnings-loading" />
      ) : sales.length === 0 ? (
        <Empty label="No sales yet — completed sales will appear here once your items sell." />
      ) : (
        <div className="space-y-2" data-testid="earnings-sales-list">
          {sales.map((s) => (
            <EarningsSaleRow key={s.id} sale={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function EarningsSaleRow({ sale: s }) {
  const date = s.completed_at ? new Date(s.completed_at) : null;
  const dateStr = date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "—";
  return (
    <div className="card-base p-4 flex items-center justify-between gap-3" data-testid={`earnings-sale-row-${s.id}`}>
      <div className="min-w-0">
        <div className="font-semibold text-sm text-slate-900 truncate" data-testid={`earnings-sale-title-${s.id}`}>
          {s.item_title}
        </div>
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200" data-testid={`earnings-sale-seller-${s.id}`}>
            Sold by {s.seller_name || "—"}
          </span>
          <span className="text-[11px] text-slate-500" data-testid={`earnings-sale-date-${s.id}`}>{dateStr}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200" data-testid={`earnings-sale-fulfillment-${s.id}`}>
            {s.fulfillment_label}
          </span>
        </div>
      </div>
      <div className="text-emerald-800 font-bold whitespace-nowrap" data-testid={`earnings-sale-net-${s.id}`}>
        +{formatGBP((s.net_pennies || 0) / 100)} net
      </div>
    </div>
  );
}

function HistoryListingsTab({ listings, kidsById, kidFilter = "" }) {
  // Iteration 251 — History now covers two row families:
  //   • family LISTINGS (live / sold / inactive)
  //   • family PURCHASES ("bought" rows from /parent/ledger)
  // Default view shows Sold + Bought + Live only. The "Show Inactive &
  // Archived" toggle re-admits removed / rejected / expired listings —
  // iteration 206's hard-hide becomes the default-OFF toggle state.
  const [filter, setFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  // Iteration 252 — child household filter. Initialized from the `?kid=`
  // deep link (iteration 106) and combinable with status pills + toggle.
  const [childFilter, setChildFilter] = useState(kidFilter || "all");
  const [boughtRows, setBoughtRows] = useState([]);
  const [boughtLoading, setBoughtLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/parent/ledger?scope=household");
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (!cancel) setBoughtRows(rows.filter((r) => r.role === "bought"));
      } catch {
        if (!cancel) setBoughtRows([]);
      } finally {
        if (!cancel) setBoughtLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Iteration 206 — inactive/archive classification (removed, rejected,
  // expired variants). Hidden by default; the toggle re-admits them.
  const REJECTED_STATUSES = new Set([
    "rejected", "rejected_parent", "rejected_admin",
    "removed", "deleted", "expired",
  ]);
  const isInactive = (s) => {
    const status = (s || "").toLowerCase();
    if (REJECTED_STATUSES.has(status)) return true;
    return status.startsWith("rejected_")
      || status.startsWith("deleted_")
      || status.startsWith("removed_")
      || status.startsWith("expired");
  };
  const classifyListing = (s) => {
    const status = (s || "").toLowerCase();
    if (["live", "public", "approved", "active"].includes(status)) return "live";
    if (["sold", "completed"].includes(status)) return "sold";
    if (isInactive(status)) return "inactive";
    return "other"; // pending etc. live in the Pending Approvals tab
  };

  const rows = useMemo(() => {
    const out = [];
    for (const l of (listings || [])) {
      out.push({
        key: l.id,
        kind: "listing",
        category: classifyListing(l.status),
        title: l.title,
        priceGBP: l.price,
        photo: (l.photos && l.photos[0]) || null,
        date: l.created_at || "",
        statusRaw: l.status,
        who: l.kid_alias || kidsById?.[l.kid_id]?.name || "—",
        // Iteration 266 — c2c @handle surfacing on history rows.
        whoHandle: l.kid_handle || kidsById?.[l.kid_id]?.handle || null,
        // Iteration 274 — clothing attribute summary on history rows.
        clothingSummary: clothingSummary(l) || null,
        listingId: l.id,
        kidId: l.kid_id || null,
      });
    }
    for (const r of boughtRows) {
      out.push({
        key: `b-${r.id}`,
        kind: "bought",
        category: "bought",
        title: r.item_title || "Item",
        priceGBP: (r.price_pennies || 0) / 100,
        photo: r.item_photo || null,
        date: r.created_at || "",
        statusRaw: "bought",
        who: r.buyer_name || "—",
        // Iteration 266 — c2c buyer @handle from the ledger resolver.
        whoHandle: r.buyer_handle || null,
        // Iteration 274 — clothing attribute summary on history rows.
        clothingSummary: r.clothing_summary || null,
        listingId: r.listing_id || null,
        kidId: r.buyer_id || null,
        autoApproved: !!r.auto_approved,
      });
    }
    out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, boughtRows, kidsById, kidFilter]);

  const visible = rows.filter((r) => {
    // Child household filter — matches rows where the kid is the seller
    // (listing rows) or the buyer (bought rows).
    if (childFilter !== "all" && r.kidId !== childFilter) return false;
    if (r.category === "inactive" && !showInactive) return false;
    if (r.category === "other") return false;
    if (filter === "all") return true;
    return r.category === filter;
  });

  const statusClass = (category, raw) => {
    if (category === "bought") return "bg-violet-100 text-violet-800 border-violet-200";
    if (category === "live") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (category === "sold") return "bg-sky-100 text-sky-800 border-sky-200";
    const status = (raw || "").toLowerCase();
    if (isInactive(status)) return "bg-rose-100 text-rose-800 border-rose-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };
  const statusLabel = (category, raw) => (category === "bought" ? "BOUGHT" : (raw || "—"));

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "sold", label: "Sold" },
    { key: "bought", label: "Bought" },
    { key: "live", label: "Live" },
  ];

  // Child filter options populate dynamically from the household roster.
  const KID_FILTERS = useMemo(() => [
    { key: "all", label: "All Kids" },
    ...Object.values(kidsById || {})
      .filter((k) => k && k.id)
      .map((k) => ({ key: k.id, label: k.name || "Child" })),
  ], [kidsById]);

  return (
    <div>
      {/* Iteration 278 — the floating "Household balance transfers" card
          moved to the dedicated Transfers tab; History is listings-only. */}
      {/* Sub-filter pills + inactive/archive toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="history-controls">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-2" data-testid="history-filter-bar">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                aria-pressed={filter === f.key}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  filter === f.key
                    ? "bg-brandSlate text-white border-brandSlate shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:border-brandSlate"
                }`}
                data-testid={`history-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="hidden sm:inline-block w-px h-5 bg-slate-200" aria-hidden="true" />
          {/* Iteration 252 — child household filter, works in combination
              with the status pills and the inactive toggle. */}
          <div className="flex flex-wrap gap-2" data-testid="history-kid-filter-bar">
            {KID_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setChildFilter(f.key)}
                aria-pressed={childFilter === f.key}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  childFilter === f.key
                    ? "bg-brandTeal text-white border-brandTeal shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:border-brandTeal"
                }`}
                data-testid={f.key === "all" ? "history-kid-filter-all" : `history-kid-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="history-inactive-toggle-label">
          <Switch
            checked={showInactive}
            onCheckedChange={setShowInactive}
            data-testid="history-inactive-toggle"
            aria-label="Show Inactive and Archived listings"
          />
          <span className="text-xs font-semibold text-slate-600">Show Inactive &amp; Archived (Removed, Rejected, Expired)</span>
        </label>
      </div>

      {visible.length === 0 ? (
        rows.length === 0 && boughtLoading ? (
          <div className="card-base p-6 text-center" data-testid="history-loading"><Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" /></div>
        ) : (
          <Empty label="No listings archived yet — every listing you post will appear here." />
        )
      ) : (
        <div className="card-base overflow-x-auto" data-testid="history-listings">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Item</th>
                <th className="text-left px-4 py-3 font-bold">Title</th>
                <th className="text-right px-4 py-3 font-bold">Price</th>
                <th className="text-left px-4 py-3 font-bold">Date</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Seller / Buyer</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.key} className="border-t border-slate-100" data-testid={r.kind === "bought" ? `history-bought-row-${r.key.slice(2)}` : `history-listing-row-${r.listingId}`}>
                  <td className="px-4 py-2">
                    {r.photo ? (
                      <img src={r.photo} alt="" className="w-12 h-12 rounded-xl object-cover border border-slate-200" data-testid={r.kind === "bought" ? `history-bought-thumb-${r.key.slice(2)}` : `history-listing-thumb-${r.listingId}`} />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-[10px]">No photo</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-900 font-medium">
                    {r.listingId ? (
                      <Link to={`/listing/${r.listingId}`} className="hover:underline" data-testid={r.kind === "bought" ? `history-bought-title-${r.key.slice(2)}` : `history-listing-title-${r.listingId}`}>{r.title}</Link>
                    ) : (
                      <span data-testid={r.kind === "bought" ? `history-bought-title-${r.key.slice(2)}` : `history-listing-title-${r.listingId}`}>{r.title}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-brandSlate font-semibold whitespace-nowrap">{formatGBP(r.priceGBP)}</td>
                  <td className="px-4 py-2 text-[12px] text-slate-600 whitespace-nowrap" data-testid={r.kind === "bought" ? `history-bought-date-${r.key.slice(2)}` : `history-listing-date-${r.listingId}`}>
                    {(r.date || "").slice(0, 10) || "—"}
                    {r.kind === "bought" && <span className="block text-[10px] text-slate-400">purchased</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusClass(r.category, r.statusRaw)}`} data-testid={r.kind === "bought" ? `history-bought-status-${r.key.slice(2)}` : `history-listing-status-${r.listingId}`}>
                      {statusLabel(r.category, r.statusRaw)}
                    </span>
                    {r.kind === "bought" && r.autoApproved && (
                      <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-100 text-teal-800 border border-teal-200" data-testid={`history-auto-approved-${r.key.slice(2)}`} title="Completed instantly by your Auto-Approve rule">
                        Auto-Approved
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-[13px] text-slate-700" data-testid={r.kind === "bought" ? `history-bought-buyer-${r.key.slice(2)}` : `history-listing-seller-${r.listingId}`}>
                    {r.kind === "bought" ? `Bought by ${r.who}` : r.who}{r.whoHandle ? ` (@${r.whoHandle})` : ""}
                    {r.clothingSummary && (
                      <div className="text-[11px] font-bold text-rose-700 mt-0.5" data-testid={`history-clothing-summary-${r.listingId || r.key}`}>{r.clothingSummary}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TransfersTab({ cashouts = [], kidsById = {}, onCashoutsChanged, onToast }) {
  // Iteration 278 — dedicated Transfers tab. Every household money
  // movement that isn't a marketplace sale/purchase: child cash-out
  // requests ("Transfer to Parent") and wallet/allowance top-ups. Built
  // purely from existing backend responses — /parent/cashout-requests
  // (fetched by the page) and /wallet/history (fetched here).
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/wallet/history?limit=100");
        const txns = Array.isArray(data?.transactions) ? data.transactions : [];
        const TOPUP_KINDS = new Set([
          "topup", "top_up", "wallet_topup", "topup_completed",
          "transfer_to_kid", "parent_to_kid", "allowance", "pocket_money", "deposit",
        ]);
        if (!cancel) {
          setTopups(txns.filter((t) => TOPUP_KINDS.has((t.kind || t.type || "").toString().toLowerCase())));
        }
      } catch {
        if (!cancel) setTopups([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Iteration 280 — payout lifecycle toggle: approved cash-outs sit
  // "Held in Household" until the parent pays out the physical
  // cash/bank funds and marks the request "Paid".
  const [payoutBusy, setPayoutBusy] = useState("");
  // Iteration 281 — "Mark as Paid" opens a method picker (Cash Handover
  // / Bank Transfer) before the payout is confirmed.
  const [methodPickerFor, setMethodPickerFor] = useState("");
  const setPayout = async (id, status, method) => {
    setPayoutBusy(id);
    try {
      await api.post(`/parent/cashout-requests/${id}/payout`, { payout_status: status, payout_method: method });
      setMethodPickerFor("");
      await onCashoutsChanged?.();
    } catch (e) {
      onToast?.(formatApiError(e) || "Couldn't update the payout state.");
    } finally {
      setPayoutBusy("");
    }
  };

  const rows = useMemo(() => {
    const out = [];
    for (const r of cashouts || []) {
      const kidName = r.kid_name || kidsById?.[r.kid_id]?.name || "Child";
      const handle = r.kid_handle ? ` (@${String(r.kid_handle).replace(/^@/, "")})` : "";
      const s = (r.status || "pending").toLowerCase();
      out.push({
        key: `cashout-${r.id}`,
        type: "Transfer to Parent",
        detail: `from ${kidName}${handle}`,
        amountGBP: (r.amount_pennies || 0) / 100,
        date: r.created_at || "",
        statusKey: s === "approved" ? "approved" : s === "declined" ? "declined" : "pending",
        statusLabel: s === "approved" ? "Approved" : s === "declined" ? "Declined" : "Pending",
        // Iteration 280 — cash-out destination label + payout lifecycle.
        destination: "Parent Household - Cash-Out",
        cashoutId: r.id,
        payoutStatus: s === "approved" ? (r.payout_status || "held") : null,
        payoutMethod: r.payout_method || null,
        // Iteration 285 — org payout gauntlet flags for the UI gate.
        orgType: r.kid_account_type === "organization",
        orgVerified: !!r.org_verified,
      });
    }
    for (const t of topups || []) {
      const statusRaw = (t.status || "").toString().toLowerCase();
      const statusKey = ["failed", "declined", "cancelled", "canceled"].includes(statusRaw)
        ? "declined"
        : ["pending", "processing"].includes(statusRaw)
          ? "pending"
          : "approved";
      const kidId = t.kid_id || t.to?.id || "";
      const kidName = kidId ? kidsById?.[kidId]?.name : null;
      out.push({
        key: `topup-${t.id || t.ref || `${t.created_at}-${t.amount_pennies}`}`,
        // Iteration 280 — top-ups landing in the household account itself
        // are "Household Top-Up"; only kid-directed ones are "Allowance".
        type: kidName ? "Allowance Top-Up" : "Household Top-Up",
        detail: kidName ? `to ${kidName}` : "",
        amountGBP: Math.abs(t.amount_pennies ?? t.delta_pennies ?? 0) / 100,
        date: t.created_at || "",
        statusKey,
        statusLabel: statusKey === "declined" ? "Failed" : statusKey === "pending" ? "Pending" : "Completed",
        destination: kidName ? `${kidName}'s Wallet` : "Parent Household Account",
      });
    }
    out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return out;
  }, [cashouts, topups, kidsById]);

  const pillClass = (k) =>
    k === "approved"
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : k === "declined"
        ? "bg-rose-100 text-rose-800 border-rose-200"
        : "bg-amber-100 text-amber-900 border-amber-200";

  if (loading) {
    return <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" data-testid="transfers-loading" />;
  }
  if (!rows.length) {
    return <Empty label="No transfers yet — cash-out requests and top-ups will appear here." />;
  }
  return (
    <div className="card-base overflow-x-auto" data-testid="transfers-tab">
      <table className="w-full text-sm" data-testid="transfers-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
          <tr>
            <th className="text-left px-4 py-3 font-bold">Type / Action</th>
            <th className="text-right px-4 py-3 font-bold">Amount</th>
            <th className="text-left px-4 py-3 font-bold">Date</th>
            <th className="text-left px-4 py-3 font-bold">Status</th>
            <th className="text-left px-4 py-3 font-bold">Destination</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-slate-100" data-testid={`transfers-row-${r.key}`}>
              <td className="px-4 py-2.5">
                <div className="font-semibold text-slate-900" data-testid={`transfers-type-${r.key}`}>{r.type}</div>
                {r.detail && <div className="text-[11px] text-slate-500">{r.detail}</div>}
              </td>
              <td className="px-4 py-2.5 text-right text-brandSlate font-semibold whitespace-nowrap" data-testid={`transfers-amount-${r.key}`}>
                {formatGBP(r.amountGBP)}
              </td>
              <td className="px-4 py-2.5 text-[12px] text-slate-600 whitespace-nowrap" data-testid={`transfers-date-${r.key}`}>
                {(r.date || "").slice(0, 10) || "—"}
              </td>
              <td className="px-4 py-2.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${pillClass(r.statusKey)}`} data-testid={`transfers-status-${r.key}`}>
                  {r.statusLabel}
                </span>
                {r.payoutStatus && (
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${r.payoutStatus === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-900 border-amber-200"}`}
                      data-testid={`transfers-payout-${r.key}`}
                    >
                      {r.payoutStatus === "paid" ? `Paid (${r.payoutMethod === "bank" ? "Bank" : "Cash"})` : "Held in Household"}
                    </span>
                    {/* Iteration 285 — org gauntlet: bank-only + verified-only. */}
                    {r.payoutStatus === "held" && !r.orgType && methodPickerFor !== r.cashoutId && (
                      <button
                        type="button"
                        onClick={() => setMethodPickerFor(r.cashoutId)}
                        className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                        data-testid={`transfers-payout-toggle-${r.key}`}
                      >
                        Mark as Paid
                      </button>
                    )}
                    {r.payoutStatus === "held" && r.orgType && r.orgVerified && (
                      <button
                        type="button"
                        onClick={() => setPayout(r.cashoutId, "paid", "bank")}
                        disabled={payoutBusy === r.cashoutId}
                        className="text-[10px] font-bold text-sky-700 hover:text-sky-900 underline underline-offset-2 disabled:opacity-50"
                        data-testid={`transfers-payout-toggle-${r.key}`}
                      >
                        {payoutBusy === r.cashoutId ? "Saving…" : "Mark as Paid (Bank)"}
                      </button>
                    )}
                    {r.payoutStatus === "held" && r.orgType && !r.orgVerified && (
                      <span className="text-[10px] font-bold text-amber-700" data-testid={`transfers-payout-org-pending-${r.key}`}>
                        Awaiting verification
                      </span>
                    )}
                    {r.payoutStatus === "paid" && (
                      <button
                        type="button"
                        onClick={() => setPayout(r.cashoutId, "held")}
                        disabled={payoutBusy === r.cashoutId}
                        className="text-[10px] font-bold text-slate-500 hover:text-slate-700 underline underline-offset-2 disabled:opacity-50"
                        data-testid={`transfers-payout-toggle-${r.key}`}
                      >
                        {payoutBusy === r.cashoutId ? "Saving…" : "Undo"}
                      </button>
                    )}
                  </div>
                )}
                {r.payoutStatus === "held" && r.orgType && (
                  <div className="mt-1 text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-2 py-1.5 max-w-xs" data-testid={`transfers-payout-org-banner-${r.key}`}>
                    Organization payouts must be transferred directly to the registered organization bank account.
                  </div>
                )}
                {r.payoutStatus === "held" && !r.orgType && methodPickerFor === r.cashoutId && (
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`transfers-payout-method-${r.key}`}>
                    <button
                      type="button"
                      onClick={() => setPayout(r.cashoutId, "paid", "cash")}
                      disabled={payoutBusy === r.cashoutId}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`transfers-payout-method-cash-${r.key}`}
                    >
                      {payoutBusy === r.cashoutId ? "Saving…" : "Cash Handover"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayout(r.cashoutId, "paid", "bank")}
                      disabled={payoutBusy === r.cashoutId}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                      data-testid={`transfers-payout-method-bank-${r.key}`}
                    >
                      {payoutBusy === r.cashoutId ? "Saving…" : "Bank Transfer"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMethodPickerFor("")}
                      disabled={payoutBusy === r.cashoutId}
                      className="text-[10px] font-bold text-slate-500 hover:text-slate-700"
                      data-testid={`transfers-payout-method-cancel-${r.key}`}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </td>
              <td className="px-4 py-2.5 text-[13px] text-slate-700" data-testid={`transfers-destination-${r.key}`}>
                {r.destination}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ParentLedger() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "pending";
  // Iteration 106 — child-specific history filter. When /kids/{id}
  // deep-links here with `?kid=<id>`, restrict the History tab to that
  // kid's listings only. Other tabs stay household-scoped.
  const kidFilter = params.get("kid") || "";
  // Iteration 306 — arrived from the Kid Zone "Total earnings from sales"
  // deep-link: surface a prominent route back to the child's shop ledger.
  const fromChildLedger = params.get("from") === "child_ledger";
  const [active, setActive] = useState(TABS.some((t) => t.key === initialTab) ? initialTab : "pending");
  const [stats, setStats] = useState(null);
  const [kids, setKids] = useState([]);
  const [listings, setListings] = useState([]);
  const [cashouts, setCashouts] = useState([]); // iteration 276
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [releasingId, setReleasingId] = useState("");
  const [editingListing, setEditingListing] = useState(null);
  const [deletingListing, setDeletingListing] = useState(null);
  // Iteration 207 — bumps the Pending Approvals inbox so it re-fetches
  // after an in-place Edit or Delete triggered from a pending card.
  const [pendingRefresh, setPendingRefresh] = useState(0);

  const kidsById = useMemo(() => {
    const map = {};
    (kids || []).forEach((k) => { map[k.id] = k; });
    return map;
  }, [kids]);

  // Iteration 280 — refetch cash-outs after a payout-state toggle.
  const refreshCashouts = async () => {
    try {
      const { data } = await api.get("/parent/cashout-requests?include_decided=true");
      setCashouts(data?.requests || []);
    } catch { /* keep old list */ }
  };

  const reloadListings = async () => {
    try {
      const { data: fresh } = await api.get("/listings/mine/all");
      setListings(fresh || []);
    } catch { /* keep old list */ }
  };

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [s, k, l, c] = await Promise.all([
          api.get("/dashboard/parent"),
          api.get("/kids"),
          api.get("/listings/mine/all"),
          // Iteration 278 — household cash-out requests for the Transfers tab.
          api.get("/parent/cashout-requests?include_decided=true").catch(() => ({ data: { requests: [] } })),
        ]);
        setStats(s.data);
        setKids(k.data || []);
        setListings(l.data || []);
        setCashouts(c.data?.requests || []);
      } catch (e) {
        setToast(formatApiError(e) || "Couldn't load ledger.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  // Cross-tab / cross-surface revalidation.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      setListings((prev) => mergeListingInto(prev, updated));
    });
  }, []);

  const changeTab = (key) => {
    setActive(key);
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  const releaseListing = async (listingId) => {
    setReleasingId(listingId);
    try {
      await api.post(`/parent/listings/${listingId}/force-release`);
      await reloadListings();
      setToast("Released — the item is back on the marketplace.");
    } catch (e) {
      setToast(formatApiError(e) || "Couldn't release.");
    } finally {
      setReleasingId("");
    }
  };

  const handleEdited = async (updated) => {
    setEditingListing(null);
    if (updated && updated.id) {
      setListings((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
    } else {
      await reloadListings();
    }
    setPendingRefresh((n) => n + 1);
    setToast("Listing updated.");
  };

  const handleDeleted = async () => {
    const title = deletingListing?.title;
    setDeletingListing(null);
    await reloadListings();
    setPendingRefresh((n) => n + 1);
    setToast(`Deleted “${title}”.`);
  };

  const liveListings = useMemo(
    () => (listings || []).filter((l) => l.status === "live" || l.status === "public"),
    [listings],
  );
  const reservedListings = useMemo(
    () => (listings || []).filter((l) => l.status === "reserved"),
    [listings],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white py-8 px-4" data-testid="parent-ledger-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/dashboard/parent"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brandSlate mb-4"
          data-testid="ledger-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        {fromChildLedger && (
          <div className="mb-4" data-testid="ledger-from-child-banner">
            <Link
              to="/kidzone/ledger?tab=earnings"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brandPink text-white text-sm font-bold shadow-md hover:bg-brandPinkDark transition"
              data-testid="ledger-back-to-child"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Child&rsquo;s Shop Ledger
            </Link>
          </div>
        )}
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-slate-900 mb-2">
          Ledger &amp; Activity
        </h1>
        <p className="text-sm text-slate-600 mb-6">
          Everything about your family&rsquo;s marketplace activity — approvals, listings, orders, earnings, and a full listing archive.
        </p>

        <TabHeader active={active} onChange={changeTab} />

        {toast && (
          <div className="mb-4 rounded-2xl bg-slate-900 text-white px-4 py-2 text-sm inline-block" data-testid="ledger-toast">
            {toast}
          </div>
        )}

        {loading && active !== "pending" ? (
          <Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto" />
        ) : (
          <>
            {active === "pending"  && (
              <PendingApprovalsInbox
                kidsById={kidsById}
                onToast={setToast}
                refreshTrigger={pendingRefresh}
                onEditListing={async (listingId) => {
                  // Iteration 207 — resolve the full listing so
                  // EditListingModal has every field (photos, categories,
                  // parcel size, weight, fulfillment_choices) — the
                  // pending-approvals payload only carries a summary.
                  try {
                    const { data } = await api.get(`/listings/${listingId}`);
                    setEditingListing(data);
                  } catch (e) {
                    setToast(formatApiError(e) || "Couldn't load that listing to edit.");
                  }
                }}
                onDeleteListing={(listingSummary) => setDeletingListing(listingSummary)}
              />
            )}
            {active === "live"     && (
              <ListingList
                listings={liveListings}
                mode="live"
                onEdit={setEditingListing}
                onDelete={setDeletingListing}
                testidPrefix="live-listings"
              />
            )}
            {active === "reserved" && (
              <ListingList
                listings={reservedListings}
                mode="reserved"
                onRelease={releaseListing}
                releasingId={releasingId}
                testidPrefix="reserved-listings"
              />
            )}
            {active === "orders"   && <OrdersTab user={user} kids={kids} />}
            {active === "earnings" && <EarningsTab stats={stats} />}
            {active === "history"  && (
              // Iteration 252 — the tab owns child filtering (initialized
              // from the ?kid= deep link), so pass the FULL listings set.
              <HistoryListingsTab
                listings={listings}
                kidsById={kidsById}
                kidFilter={kidFilter}
              />
            )}
            {active === "transfers" && (
              <TransfersTab
                cashouts={cashouts}
                kidsById={kidsById}
                onCashoutsChanged={refreshCashouts}
                onToast={setToast}
              />
            )}
          </>
        )}

        {editingListing && (
          <EditListingModal
            listing={editingListing}
            onClose={() => setEditingListing(null)}
            onSaved={handleEdited}
          />
        )}
        {deletingListing && (
          <ConfirmDeleteModal
            listing={deletingListing}
            onClose={() => setDeletingListing(null)}
            onConfirmed={handleDeleted}
          />
        )}
      </div>
    </div>
  );
}
